import numbro from 'numbro';
import React, { useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaTable } from 'react-icons/fa';

import ErrorBoundary from '../../../components/general/ErrorBoundary';
import LoadingImage from '../../../components/general/LoadingImage';
import SimpleLineChart from '../../../components/SimpleLineChart';
import DLTable from '../../../components/table/DLTable/DLTable';
import { uiHelpers } from '../../../helpers';
import { useNavigateCCASS } from '../../../helpers/ui.helper';
import { ReactTable } from '../../../types';

export interface CCASSHistoryProps {
  stockCode: string;
  accessors: ReactTable.Accessor[];
  labels: string[];
  title: string;
  formatters: (string | Record<string, unknown>)[];
  onHide: () => void;
  requestFn: () => Promise<any[]>;
}

const CCASSHistory: React.FC<CCASSHistoryProps> = React.memo(
  function CCASSHistory({
    stockCode,
    accessors,
    labels,
    title,
    formatters,
    onHide,
    requestFn,
  }) {
    const [records, setRecords] = useState<any[]>();
    const [loading, setLoading] = useState<boolean>(false);

    const [t] = useTranslation();

    const onNavigateCCASS = useNavigateCCASS();

    useEffect(() => {
      setLoading(true);
      requestFn().then(
        data => {
          setLoading(false);
          setRecords(data);
        },
        () => {
          setLoading(false);
        }
      );
    }, [requestFn]);

    if (records) {
      for (const [i, record] of records.entries())
        record.previous = i === 0 ? {} : records[i - 1];
    }

    const columns: ReactTable.Column[] = [
      {
        id: 'ccass-his-date',
        Header: t('Date'),
        accessor: 'Date',
        width: 90,
        Cell: function DateCell({ cell: { value } }) {
          const date = uiHelpers.formatDateLocal(value.toString());
          return (
            <div
              onClick={() => {
                onHide();
                onNavigateCCASS(stockCode, date);
              }}
              style={{ cursor: 'pointer' }}
            >
              <u>{date}</u>
            </div>
          );
        },
        sortDescFirst: true,
        disableFilters: true,
      },
      ...accessors.map((_, i) => {
        const formatter = v => numbro(v).format(formatters[i]);
        return {
          id: `ccass-his-value-${i}`,
          Header: labels[i],
          accessor: accessors[i],
          width: 120,
          Cell: function ValueCell({ cell: { value } }) {
            return <div>{formatter(value)}</div>;
          },
          sortDescFirst: true,
        };
      }),
    ];

    return (
      <Modal show={true} size="lg" centered onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>
            {stockCode}: {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="raw" mountOnEnter>
            <Tab
              eventKey="raw"
              title={
                <small>
                  <FaTable /> {t('Table')}
                </small>
              }
            >
              <Row>
                <Col md={12}>
                  <DLTable
                    tableId="ccass-history"
                    columns={columns}
                    data={records || []}
                    defaultSortBy={[
                      {
                        id: 'ccass-his-date',
                        desc: true,
                      },
                    ]}
                    loading={loading}
                  />
                </Col>
              </Row>
            </Tab>
            {accessors.map((_, i) => (
              <Tab
                key={i}
                eventKey={`t-${i}`}
                title={
                  <small>
                    <FaChartLine /> {labels[i]}
                  </small>
                }
              >
                <Row>
                  <Col md={{ span: 12, offset: 0 }}>
                    {records && records.length > 0 && (
                      <ErrorBoundary>
                        <SimpleLineChart
                          values={records.map(r => {
                            return {
                              date: r.Date,
                              value:
                                typeof accessors[i] === 'string'
                                  ? r[accessors[i] as string]
                                  : (
                                      accessors[
                                        i
                                      ] as ReactTable.AccessorFunction
                                    )(r),
                            };
                          })}
                          title={labels[i]}
                          formatter={formatters[i]}
                        />
                      </ErrorBoundary>
                    )}
                    <LoadingImage displayAsBlock={true} loading={loading} />
                  </Col>
                </Row>
              </Tab>
            ))}
          </Tabs>
        </Modal.Body>
      </Modal>
    );
  }
);

export default CCASSHistory;
